import React, { useState } from 'react';
import { Bell, X } from 'lucide-react';
import { requestNotificationPermission, isNotificationSupported } from '../services/fcmService';

interface NotificationPermissionRequestProps {
  userId: string;
  onClose: () => void;
}

const NotificationPermissionRequest: React.FC<NotificationPermissionRequestProps> = ({
  userId,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isNotificationSupported()) {
    return null;
  }

  const handleEnable = async () => {
    setLoading(true);
    setError(null);
    try {
      const granted = await requestNotificationPermission();
      if (granted) {
        // Save FCM token in background (non-blocking)
        const { initFCM } = await import('../services/fcmService');
        initFCM(userId).catch((err) => {
          console.error('Background FCM token save failed:', err);
          // Silently fail - don't block the user experience
        });
        
        // Close immediately after permission is granted
        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        setError('Notification permission was denied. Please enable notifications in your browser settings.');
      }
    } catch (err: any) {
      console.error('Error requesting notification permission:', err);
      setError('An error occurred while requesting notification permission. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLater = () => {
    setDeclined(true);
    setLoading(false); // Always allow Ask Later to work
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-3 rounded-full">
              <Bell className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Stay Connected</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {!declined ? (
            <>
              <p className="text-gray-700">
                Enable notifications to receive real-time updates about:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1">❤️</span>
                  <span className="text-gray-700">Likes and comments on your posts</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1">💬</span>
                  <span className="text-gray-700">Direct messages from family members</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1">👋</span>
                  <span className="text-gray-700">New members joining the family</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-blue-600 mt-1">🎂</span>
                  <span className="text-gray-700">Birthdays and anniversaries</span>
                </li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-blue-900">
                  💡 You can customize notification preferences anytime in settings
                </p>
              </div>
              {error && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 mt-4 text-rose-700 text-sm">
                  {error}
                  <button
                    className="ml-2 text-indigo-600 underline text-xs font-bold"
                    onClick={handleEnable}
                    disabled={loading}
                  >
                    Retry
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-700 mb-2">You can enable notifications anytime in settings</p>
              <p className="text-sm text-gray-500">Redirecting...</p>
            </div>
          )}
        </div>

        {/* Actions */}
        {!declined ? (
          <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <button
              onClick={handleLater}
              className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium"
              disabled={false}
            >
              Ask Later
            </button>
            <button
              onClick={handleEnable}
              className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="animate-spin">⏳</div>
                  Enabling...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4" />
                  Enable Notifications
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg text-center">
            <p className="text-sm text-gray-600">Thank you! Check your settings anytime.</p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default NotificationPermissionRequest;
